import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";
import { SalarySlip } from "../../types/models";
import { SalarySlipContextType } from "../../types/context";

export const SalarySlipContext = createContext<SalarySlipContextType | undefined>(undefined);

interface SalarySlipProviderProps {
  children: ReactNode;
}

export const SalarySlipProvider: React.FC<SalarySlipProviderProps> = ({ children }) => {
  const [salarySlips, setSalarySlips] = useState<SalarySlip[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSalarySlips = useCallback(async (
    employeeId: number | null = null,
    projectId: number | null = null,
    page: number = 1,
    perPage: number = 15
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      let url = '/salary-slips';
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (employeeId) params.append('employee_id', employeeId.toString());
      if (projectId) params.append('project_id', projectId.toString());
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let salarySlipsData: SalarySlip[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          salarySlipsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          salarySlipsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          salarySlipsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        salarySlipsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        salarySlipsData = responseData;
      }
      
      setSalarySlips(salarySlipsData as SalarySlip[]);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      console.error("Failed to fetch salary slips:", err);
      setSalarySlips([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createSalarySlip = useCallback(async (salarySlipData: any): Promise<SalarySlip> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<SalarySlip>>('/salary-slips', salarySlipData);
      const newSalarySlip = response.data.data;
      setSalarySlips((prev) => [...prev, newSalarySlip]);
      return newSalarySlip;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSalarySlip = useCallback(async (id: number, salarySlipData: any): Promise<SalarySlip> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<SalarySlip>>(`/salary-slips/${id}`, salarySlipData);
      const updatedSalarySlip = response.data.data;
      setSalarySlips((prev) =>
        prev.map((slip) => (slip.id === id ? updatedSalarySlip : slip))
      );
      return updatedSalarySlip;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSalarySlip = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/salary-slips/${id}`);
      setSalarySlips((prev) => prev.filter((slip) => slip.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSalarySlip = useCallback(async (id: number): Promise<SalarySlip> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiResponse<SalarySlip>>(`/salary-slips/${id}`);
      return response.data.data;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEmployeeSalarySlips = useCallback(async (employeeId: number): Promise<SalarySlip[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/employees/${employeeId}/salary-slips`);
      // Handle nested response structure: response.data.data.data or response.data.data
      let salarySlipsData: SalarySlip[] = [];
      if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        // Nested structure: { success: true, data: { data: [...], pagination: {...} } }
        salarySlipsData = response.data.data.data;
      } else if (Array.isArray(response.data?.data)) {
        // Direct array in data: { success: true, data: [...] }
        salarySlipsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array response
        salarySlipsData = response.data;
      }
      return salarySlipsData as SalarySlip[];
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const contextValue: SalarySlipContextType = {
    salarySlips,
    pagination: pagination || undefined,
    loading,
    error,
    fetchSalarySlips,
    createSalarySlip,
    updateSalarySlip,
    deleteSalarySlip,
    getSalarySlip,
    getEmployeeSalarySlips,
  };

  return (
    <SalarySlipContext.Provider value={contextValue}>
      {children}
    </SalarySlipContext.Provider>
  );
};

export default SalarySlipProvider;

